import { defineMessages } from './define'

/* The admin screens: the admin hub (reminders, backups, audit), clinic
 * settings, staff logins, the doctor roster and doctor schedules.
 *
 * Letterhead and calibration previews show what will be printed, so they
 * stay English; only the form around them is translated. */

export default defineMessages({
  en: {
    // admin hub
    'admin.title': 'Admin',
    'admin.tabReminders': 'Reminders',
    'admin.tabBackups': 'Backups',
    'admin.tabAudit': 'Audit',
    'admin.agoSec': '{n} s ago',
    'admin.agoMin': '{n} min ago',
    'admin.agoHour': '{n} h ago',
    'admin.agoDay': '{n} d ago',
    'admin.generate3Days': 'Generate 3 days',
    'admin.sendDue': 'Send due',
    'admin.provider': 'Provider: {provider}',
    'admin.providerLogNote': '— messages are recorded to the server log rather than sent.',
    'admin.queued': 'Queued {followUps} follow-ups and {appointments} appointment reminders.',
    'admin.generateFailed': 'Could not generate.',
    'admin.sentSummary': 'Sent {sent}; {failed} failed.',
    'admin.sendFailed': 'Could not send.',
    'admin.cancelFailed': 'Could not cancel.',
    'admin.backupWritten': 'Backup written: {file} ({size}).',
    'admin.backupFailed': 'Backup failed.',
    'admin.downloadFailed': 'Download failed.',
    'admin.deleteFailed': 'Delete failed.',
    'admin.restored': 'Restored from {file}. You may need to sign in again.',
    'admin.restoreFailed': 'Restore failed.',
    'admin.nothingQueued': 'Nothing queued.',
    'admin.takeBackup': 'Take a backup now',
    'admin.backupDirNote': 'Written to {dir}. Test the restore quarterly — a backup nobody has restored is a guess.',
    'admin.noBackups': 'No backups yet.',
    'admin.restore': 'Restore',
    'admin.auditFilterAria': 'Filter the audit log by entity',
    'admin.auditEntityPh': 'Entity — patients, visits, prescriptions…',
    'admin.clear': 'Clear',
    'admin.from': 'From',
    'admin.to': 'To',
    'admin.userId': 'User id',
    'admin.fullUuid': 'Full uuid',
    'admin.showingOf': 'Showing {shown} of {total}',
    'admin.noEntries': 'No entries match.',
    'admin.system': 'system',
    'admin.before': 'Before',
    'admin.after': 'After',
    'admin.cancelReminderTitle': 'Cancel this reminder?',
    'admin.cancelReminder': 'Cancel reminder',
    'admin.deleteBackupTitle': 'Delete this backup?',
    'admin.deleteBackupNote': '{file}. This does not touch the database.',
    'admin.restoreTitle': 'Restore from backup',
    'admin.restoreWarning': 'This wipes the current database and replays the file. Everyone signed in is signed out.',
    'admin.typeRestore': 'Type RESTORE to confirm',
    'admin.restoreNow': 'Restore now',

    // clinic settings
    'admin.settingsSaved': 'Clinic settings saved.',
    'admin.couldNotSave': 'Could not save.',
    'admin.clinicSettings': 'Clinic settings',
    'admin.saveSettings': 'Save settings',
    'admin.identity': 'Identity',
    'admin.clinicName': 'Clinic name',
    'admin.opdTimings': 'OPD timings',
    'admin.addressLine1': 'Address line 1',
    'admin.addressLine2': 'Address line 2',
    'admin.city': 'City',
    'admin.state': 'State',
    'admin.pin': 'PIN',
    'admin.phone': 'Phone',
    'admin.altPhone': 'Alternate phone',
    'admin.numbering': 'Numbering & scheduling',
    'admin.numberingNote': 'Receipt numbers restart each financial year. April is the Indian default.',
    'admin.patientCodePrefix': 'Patient code prefix',
    'admin.example': 'e.g. {example}',
    'admin.receiptPrefix': 'Receipt prefix',
    'admin.fyStart': 'FY starts (month)',
    'admin.slotLength': 'Slot length (min)',
    'admin.currency': 'Currency',

    // staff logins
    'admin.loginCreated': 'Login created. The user must change the password at first sign-in.',
    'admin.createLoginFailed': 'Could not create the login.',
    'admin.userDeactivated': '{name} deactivated.',
    'admin.userReactivated': '{name} reactivated.',
    'admin.couldNotUpdate': 'Could not update.',
    'admin.passwordResetDone': 'Password reset.',
    'admin.resetFailed': 'Could not reset the password.',
    'admin.creating': 'Creating…',
    'admin.createLogin': 'Create login',
    'admin.newLogin': 'New login',
    'admin.fullName': 'Full name',
    'admin.username': 'Username',
    'admin.tempPassword': 'Temporary password',
    'admin.passwordHint': 'At least 8 characters, not all digits. The user changes it at first sign-in.',
    'admin.passwordHintShort': 'At least 8 characters, not all digits.',
    'admin.role': 'Role',
    'admin.role.admin': 'Admin',
    'admin.role.platform_admin': 'Platform admin',
    'admin.role.doctor': 'Doctor',
    'admin.role.receptionist': 'Receptionist',
    'admin.role.lab_manager': 'Lab manager',
    'admin.role.patient': 'Patient',
    'admin.linkedDoctor': 'Linked doctor record',
    'admin.selectDoctor': 'Select a doctor…',
    'admin.addLogin': 'Add a login',
    'admin.staffLogins': 'Staff logins',
    'admin.staffNote': 'Deactivating a login signs that person out everywhere immediately. Accounts are never deleted, so the audit trail stays intact.',
    'admin.active': 'active',
    'admin.inactive': 'inactive',
    'admin.lastSignedIn': 'Last signed in {date}',
    'admin.neverSignedIn': 'Never signed in',
    'admin.onTempPassword': 'On a temporary password',
    'admin.resetPassword': 'Reset password',
    'admin.deactivate': 'Deactivate',
    'admin.reactivate': 'Reactivate',
    'admin.noLogins': 'No logins yet.',
    'admin.resetNote': '{name} is signed out everywhere and must set their own password at next sign-in.',
    'admin.newTempPassword': 'New temporary password',
    'admin.reset': 'Reset',

    // doctor roster
    'admin.openDoctorFailed': 'Could not open that doctor.',
    'admin.doctorAdded': 'Doctor added.',
    'admin.letterheadSaved': 'Letterhead settings saved.',
    'admin.addDoctorCta': 'Add a doctor',
    'admin.addDoctor': 'Add doctor',
    'admin.noDoctors': 'No doctors yet.',
    'admin.saveChanges': 'Save changes',
    'admin.newDoctor': 'New doctor',
    'admin.editDoctor': 'Edit doctor',
    'admin.qualifications': 'Qualifications',
    'admin.regNo': 'Medical council registration no.',
    'admin.regNoHint': 'Printed in the letterhead header and the signature block.',
    'admin.specialisation': 'Specialisation',
    'admin.photoUrl': 'Photo URL',
    'admin.photoHint': 'Shown on the doctor’s profile in the app. Left empty, their initials stand in.',
    'admin.about': 'About',
    'admin.aboutPh': 'A few sentences a patient reads before choosing this doctor.',
    'admin.consultFee': 'Consultation fee',
    'admin.followUpFee': 'Follow-up fee',
    'admin.followUpWindow': 'Follow-up window (days)',
    'admin.followUpWindowHint': 'A repeat visit inside this many days bills at the follow-up fee.',
    'admin.activeOnRoster': 'Active on the roster',
    'admin.letterhead': 'Letterhead',
    'admin.letterheadNote': 'Set pre-printed if the clinic already owns printed paper; only the body is rendered, into the blank area. Margins are millimetres on a 210 × 297 mm sheet.',
    'admin.printMode': 'Print mode',
    'admin.modeFull': 'Full print on blank A4',
    'admin.modePreprinted': 'Pre-printed stationery (body only)',
    'admin.marginTop': 'Top (mm)',
    'admin.marginBottom': 'Bottom (mm)',
    'admin.marginLeft': 'Left (mm)',
    'admin.marginRight': 'Right (mm)',
    'admin.headerNote': 'Header note',
    'admin.footerNote': 'Footer note',
    'admin.printVitals': 'Print the vitals strip',
    'admin.printAllergy': 'Print the allergy banner',
    'admin.saveLetterhead': 'Save letterhead settings',
    'admin.calibration': 'Calibration',
    'admin.calibrationNote': 'The test print puts the zone boxes and a millimetre ruler on one page. Run it on a sheet of the clinic’s own letterhead to set the margins in one go. Easiest from a desktop attached to the clinic printer.',
    'admin.hideTestPrint': 'Hide test print',
    'admin.showTestPrint': 'Show test print',
    'admin.printSheet': 'Print this sheet',

    // schedule
    'admin.schedule': 'Schedule',
    'admin.errNeedTimes': 'Give a start and an end time.',
    'admin.errEndAfterStart': 'The end must be after the start.',
    'admin.errSlotRange': 'Slots are 2 to 120 minutes.',
    'admin.errCapRange': 'Between 1 and 200, or blank for no cap.',
    'admin.errPastDate': 'Pick today or a later date.',
    'admin.sessionAdded': 'Session added to the weekly template.',
    'admin.addSessionFailed': 'Could not add the session.',
    'admin.overrideClash_one': 'Saved. {n} booking needs moving.',
    'admin.overrideClash_other': 'Saved. {n} bookings need moving.',
    'admin.overrideClear': 'Saved. Nothing was booked that day.',
    'admin.overrideFailed': 'Could not save that override.',
    'admin.holidaySaved': 'Holiday saved.',
    'admin.holidayFailed': 'Could not save that holiday.',
    'admin.removed': 'Removed. Slots rebuild from the next load.',
    'admin.removeFailed': 'Could not remove that.',
    'admin.addSession': 'Add a session',
    'admin.addLeave': 'Add leave or changed hours',
    'admin.addHoliday': 'Add a holiday',
    'admin.tabWeekly': 'Weekly',
    'admin.tabLeave': 'Leave & overrides',
    'admin.tabHolidays': 'Holidays',
    'admin.noDoctorsRoster': 'No doctors on the roster yet.',
    'admin.weeklyNote': 'The template repeats every week. Leave and holidays are subtracted from it when the slots are built.',
    'admin.noWeeklyTitle': 'No weekly sessions',
    'admin.noWeeklyBody': 'Nothing can be booked for this doctor until a session exists.',
    'admin.minSlots': '{n} min slots',
    'admin.noCap': 'no cap',
    'admin.capN': 'cap {n}',
    'admin.off': 'off',
    'admin.nothingScheduledOn': 'Nothing scheduled on {days}.',
    'admin.clashes_one': '{n} booking already sits on that day. Ring this patient.',
    'admin.clashes_other': '{n} bookings already sit on that day. Ring these patients.',
    'admin.allPhoned': 'All phoned, clear this',
    'admin.overridesNote': 'Dated changes for one doctor, from today onward. A closed day beats the weekly template.',
    'admin.noLeaveTitle': 'No leave booked',
    'admin.noLeaveBody': 'This doctor keeps to the weekly template.',
    'admin.closedAllDay': 'Closed all day',
    'admin.onLeave': 'On leave',
    'admin.changedHours': 'Changed hours',
    'admin.holidaysNote': 'Holidays close the whole clinic, every doctor at once. For one doctor being away, use leave instead. Saving a date that already exists renames it rather than adding a second.',
    'admin.noHolidaysTitle': 'No holidays ahead',
    'admin.noHolidaysBody': 'Add the festival list once and the slot grids follow it.',
    'admin.addWeeklySession': 'Add a weekly session',
    'admin.repeatsWeekly': 'Repeats every week until it is removed.',
    'admin.day': 'Day',
    'admin.session': 'Session',
    'admin.sessionHint': 'e.g. Morning OPD',
    'admin.starts': 'Starts',
    'admin.ends': 'Ends',
    'admin.slotMin': 'Slot (min)',
    'admin.maxPatients': 'Max patients',
    'admin.noCapPh': 'No cap',
    'admin.capHint': 'Blank means the hours decide.',
    'admin.addSessionBtn': 'Add session',
    'admin.leaveOrHours': 'Leave or changed hours',
    'admin.leaveSheetNote': 'One date, this doctor only. Anything already booked is listed after saving.',
    'admin.reasonPh': 'Conference, surgery list, personal leave',
    'admin.reasonHint': 'Shown to whoever is covering the desk.',
    'admin.holidaySheetNote': 'Closes every doctor on that date.',
    'admin.holidayNamePh': 'Diwali',
    'admin.addHolidayBtn': 'Add holiday',
    'admin.removeTitle': 'Remove this?',
    'admin.removeNote': 'Appointments already booked are left alone. Only the slots on offer change.',
  },
  hi: {
    'admin.title': 'एडमिन',
    'admin.tabReminders': 'रिमाइंडर',
    'admin.tabBackups': 'बैकअप',
    'admin.tabAudit': 'ऑडिट',
    'admin.agoSec': '{n} सेकंड पहले',
    'admin.agoMin': '{n} मिनट पहले',
    'admin.agoHour': '{n} घंटे पहले',
    'admin.agoDay': '{n} दिन पहले',
    'admin.generate3Days': '3 दिन के बनाएँ',
    'admin.sendDue': 'बाकी भेजें',
    'admin.provider': 'प्रोवाइडर: {provider}',
    'admin.providerLogNote': '— संदेश भेजे नहीं जाते, सर्वर लॉग में दर्ज होते हैं।',
    'admin.queued': '{followUps} फ़ॉलो-अप और {appointments} अपॉइंटमेंट रिमाइंडर कतार में लगाए गए।',
    'admin.generateFailed': 'बनाया नहीं जा सका।',
    'admin.sentSummary': '{sent} भेजे गए; {failed} विफल।',
    'admin.sendFailed': 'भेजा नहीं जा सका।',
    'admin.cancelFailed': 'रद्द नहीं हो सका।',
    'admin.backupWritten': 'बैकअप बन गया: {file} ({size})।',
    'admin.backupFailed': 'बैकअप नहीं हो सका।',
    'admin.downloadFailed': 'डाउनलोड नहीं हो सका।',
    'admin.deleteFailed': 'हटाया नहीं जा सका।',
    'admin.restored': '{file} से रीस्टोर हो गया। आपको फिर से साइन इन करना पड़ सकता है।',
    'admin.restoreFailed': 'रीस्टोर नहीं हो सका।',
    'admin.nothingQueued': 'कतार में कुछ नहीं है।',
    'admin.takeBackup': 'अभी बैकअप लें',
    'admin.backupDirNote': 'यहाँ सेव होता है: {dir}। हर तीन महीने में रीस्टोर करके जाँचें — जिस बैकअप को कभी रीस्टोर करके नहीं देखा, वह सिर्फ़ अंदाज़ा है।',
    'admin.noBackups': 'अभी कोई बैकअप नहीं है।',
    'admin.restore': 'रीस्टोर',
    'admin.auditFilterAria': 'ऑडिट लॉग को एंटिटी से फ़िल्टर करें',
    'admin.auditEntityPh': 'एंटिटी — patients, visits, prescriptions…',
    'admin.clear': 'साफ़ करें',
    'admin.from': 'कब से',
    'admin.to': 'कब तक',
    'admin.userId': 'यूज़र आईडी',
    'admin.fullUuid': 'पूरा uuid',
    'admin.showingOf': '{total} में से {shown} दिखाए जा रहे हैं',
    'admin.noEntries': 'कोई एंट्री मेल नहीं खाती।',
    'admin.system': 'सिस्टम',
    'admin.before': 'पहले',
    'admin.after': 'बाद में',
    'admin.cancelReminderTitle': 'यह रिमाइंडर रद्द करें?',
    'admin.cancelReminder': 'रिमाइंडर रद्द करें',
    'admin.deleteBackupTitle': 'यह बैकअप हटाएँ?',
    'admin.deleteBackupNote': '{file}। इससे डेटाबेस पर कोई असर नहीं पड़ता।',
    'admin.restoreTitle': 'बैकअप से रीस्टोर करें',
    'admin.restoreWarning': 'इससे मौजूदा डेटाबेस मिट जाएगा और यह फ़ाइल फिर से लोड होगी। जो भी साइन इन है, सब साइन आउट हो जाएँगे।',
    'admin.typeRestore': 'पक्का करने के लिए RESTORE टाइप करें',
    'admin.restoreNow': 'अभी रीस्टोर करें',

    'admin.settingsSaved': 'क्लिनिक सेटिंग्स सेव हो गईं।',
    'admin.couldNotSave': 'सेव नहीं हो सका।',
    'admin.clinicSettings': 'क्लिनिक सेटिंग्स',
    'admin.saveSettings': 'सेटिंग्स सेव करें',
    'admin.identity': 'पहचान',
    'admin.clinicName': 'क्लिनिक का नाम',
    'admin.opdTimings': 'OPD का समय',
    'admin.addressLine1': 'पता पंक्ति 1',
    'admin.addressLine2': 'पता पंक्ति 2',
    'admin.city': 'शहर',
    'admin.state': 'राज्य',
    'admin.pin': 'PIN कोड',
    'admin.phone': 'फ़ोन',
    'admin.altPhone': 'दूसरा फ़ोन',
    'admin.numbering': 'नंबरिंग और शेड्यूल',
    'admin.numberingNote': 'रसीद नंबर हर वित्त वर्ष में फिर से शुरू होते हैं। भारत में आम तौर पर वित्त वर्ष अप्रैल से शुरू होता है।',
    'admin.patientCodePrefix': 'मरीज़ कोड प्रीफ़िक्स',
    'admin.example': 'जैसे {example}',
    'admin.receiptPrefix': 'रसीद प्रीफ़िक्स',
    'admin.fyStart': 'वित्त वर्ष की शुरुआत (महीना)',
    'admin.slotLength': 'स्लॉट की अवधि (मिनट)',
    'admin.currency': 'मुद्रा',

    'admin.loginCreated': 'लॉगिन बन गया। पहली बार साइन इन करते समय यूज़र को पासवर्ड बदलना होगा।',
    'admin.createLoginFailed': 'लॉगिन नहीं बन सका।',
    'admin.userDeactivated': '{name} को निष्क्रिय किया गया।',
    'admin.userReactivated': '{name} को फिर से सक्रिय किया गया।',
    'admin.couldNotUpdate': 'अपडेट नहीं हो सका।',
    'admin.passwordResetDone': 'पासवर्ड रीसेट हो गया।',
    'admin.resetFailed': 'पासवर्ड रीसेट नहीं हो सका।',
    'admin.creating': 'बन रहा है…',
    'admin.createLogin': 'लॉगिन बनाएँ',
    'admin.newLogin': 'नया लॉगिन',
    'admin.fullName': 'पूरा नाम',
    'admin.username': 'यूज़रनेम',
    'admin.tempPassword': 'अस्थायी पासवर्ड',
    'admin.passwordHint': 'कम से कम 8 अक्षर, सभी अंक नहीं। यूज़र पहली बार साइन इन करते समय इसे बदलेगा।',
    'admin.passwordHintShort': 'कम से कम 8 अक्षर, सभी अंक नहीं।',
    'admin.role': 'भूमिका',
    'admin.role.admin': 'एडमिन',
    'admin.role.platform_admin': 'प्लेटफ़ॉर्म एडमिन',
    'admin.role.doctor': 'डॉक्टर',
    'admin.role.receptionist': 'रिसेप्शनिस्ट',
    'admin.role.lab_manager': 'लैब मैनेजर',
    'admin.role.patient': 'मरीज़',
    'admin.linkedDoctor': 'जुड़ा हुआ डॉक्टर रिकॉर्ड',
    'admin.selectDoctor': 'डॉक्टर चुनें…',
    'admin.addLogin': 'लॉगिन जोड़ें',
    'admin.staffLogins': 'स्टाफ लॉगिन',
    'admin.staffNote': 'लॉगिन निष्क्रिय करते ही वह व्यक्ति हर जगह से तुरंत साइन आउट हो जाता है। अकाउंट कभी हटाए नहीं जाते, इसलिए ऑडिट रिकॉर्ड पूरा रहता है।',
    'admin.active': 'सक्रिय',
    'admin.inactive': 'निष्क्रिय',
    'admin.lastSignedIn': 'पिछला साइन इन {date}',
    'admin.neverSignedIn': 'कभी साइन इन नहीं किया',
    'admin.onTempPassword': 'अस्थायी पासवर्ड पर',
    'admin.resetPassword': 'पासवर्ड रीसेट करें',
    'admin.deactivate': 'निष्क्रिय करें',
    'admin.reactivate': 'फिर से सक्रिय करें',
    'admin.noLogins': 'अभी कोई लॉगिन नहीं है।',
    'admin.resetNote': '{name} हर जगह से साइन आउट हो जाएँगे और अगली बार साइन इन पर उन्हें अपना पासवर्ड बनाना होगा।',
    'admin.newTempPassword': 'नया अस्थायी पासवर्ड',
    'admin.reset': 'रीसेट करें',

    'admin.openDoctorFailed': 'इस डॉक्टर की जानकारी नहीं खुल सकी।',
    'admin.doctorAdded': 'डॉक्टर जोड़ दिए गए।',
    'admin.letterheadSaved': 'लेटरहेड सेटिंग्स सेव हो गईं।',
    'admin.addDoctorCta': 'डॉक्टर जोड़ें',
    'admin.addDoctor': 'डॉक्टर जोड़ें',
    'admin.noDoctors': 'अभी कोई डॉक्टर नहीं है।',
    'admin.saveChanges': 'बदलाव सेव करें',
    'admin.newDoctor': 'नए डॉक्टर',
    'admin.editDoctor': 'डॉक्टर की जानकारी बदलें',
    'admin.qualifications': 'योग्यताएँ',
    'admin.regNo': 'मेडिकल काउंसिल रजिस्ट्रेशन नं.',
    'admin.regNoHint': 'लेटरहेड के हेडर और हस्ताक्षर वाले हिस्से में छपता है।',
    'admin.specialisation': 'विशेषज्ञता',
    'admin.photoUrl': 'फ़ोटो URL',
    'admin.photoHint': 'ऐप में डॉक्टर की प्रोफ़ाइल पर दिखती है। खाली छोड़ने पर उनके नाम के पहले अक्षर दिखेंगे।',
    'admin.about': 'परिचय',
    'admin.aboutPh': 'कुछ वाक्य, जिन्हें मरीज़ यह डॉक्टर चुनने से पहले पढ़ते हैं।',
    'admin.consultFee': 'परामर्श फ़ीस',
    'admin.followUpFee': 'फ़ॉलो-अप फ़ीस',
    'admin.followUpWindow': 'फ़ॉलो-अप अवधि (दिन)',
    'admin.followUpWindowHint': 'इतने दिनों के अंदर दोबारा आने पर फ़ॉलो-अप फ़ीस लगती है।',
    'admin.activeOnRoster': 'रोस्टर पर सक्रिय',
    'admin.letterhead': 'लेटरहेड',
    'admin.letterheadNote': 'अगर क्लिनिक के पास पहले से छपा हुआ कागज़ है तो प्री-प्रिंटेड चुनें; तब सिर्फ़ मुख्य हिस्सा खाली जगह में छपेगा। मार्जिन 210 × 297 mm शीट पर मिलीमीटर में हैं।',
    'admin.printMode': 'प्रिंट मोड',
    'admin.modeFull': 'खाली A4 पर पूरा प्रिंट',
    'admin.modePreprinted': 'प्री-प्रिंटेड स्टेशनरी (सिर्फ़ मुख्य हिस्सा)',
    'admin.marginTop': 'ऊपर (mm)',
    'admin.marginBottom': 'नीचे (mm)',
    'admin.marginLeft': 'बाएँ (mm)',
    'admin.marginRight': 'दाएँ (mm)',
    'admin.headerNote': 'हेडर नोट',
    'admin.footerNote': 'फ़ुटर नोट',
    'admin.printVitals': 'वाइटल्स की पट्टी प्रिंट करें',
    'admin.printAllergy': 'एलर्जी बैनर प्रिंट करें',
    'admin.saveLetterhead': 'लेटरहेड सेटिंग्स सेव करें',
    'admin.calibration': 'कैलिब्रेशन',
    'admin.calibrationNote': 'टेस्ट प्रिंट में ज़ोन बॉक्स और मिलीमीटर स्केल एक ही पेज पर आते हैं। मार्जिन एक बार में सेट करने के लिए इसे क्लिनिक के अपने लेटरहेड की शीट पर छापें। क्लिनिक के प्रिंटर से जुड़े डेस्कटॉप से यह सबसे आसान है।',
    'admin.hideTestPrint': 'टेस्ट प्रिंट छिपाएँ',
    'admin.showTestPrint': 'टेस्ट प्रिंट दिखाएँ',
    'admin.printSheet': 'यह शीट प्रिंट करें',

    'admin.schedule': 'शेड्यूल',
    'admin.errNeedTimes': 'शुरू और खत्म होने का समय दें।',
    'admin.errEndAfterStart': 'खत्म होने का समय शुरू के बाद होना चाहिए।',
    'admin.errSlotRange': 'स्लॉट 2 से 120 मिनट के होते हैं।',
    'admin.errCapRange': '1 से 200 के बीच, या कोई सीमा न हो तो खाली छोड़ें।',
    'admin.errPastDate': 'आज या आगे की तारीख़ चुनें।',
    'admin.sessionAdded': 'सेशन साप्ताहिक टेम्पलेट में जुड़ गया।',
    'admin.addSessionFailed': 'सेशन नहीं जुड़ सका।',
    'admin.overrideClash_one': 'सेव हो गया। {n} बुकिंग को बदलना होगा।',
    'admin.overrideClash_other': 'सेव हो गया। {n} बुकिंग को बदलना होगा।',
    'admin.overrideClear': 'सेव हो गया। उस दिन कोई बुकिंग नहीं थी।',
    'admin.overrideFailed': 'यह बदलाव सेव नहीं हो सका।',
    'admin.holidaySaved': 'अवकाश सेव हो गया।',
    'admin.holidayFailed': 'यह अवकाश सेव नहीं हो सका।',
    'admin.removed': 'हटा दिया गया। अगली बार लोड होने पर स्लॉट फिर से बनेंगे।',
    'admin.removeFailed': 'हटाया नहीं जा सका।',
    'admin.addSession': 'सेशन जोड़ें',
    'admin.addLeave': 'छुट्टी या बदला हुआ समय जोड़ें',
    'admin.addHoliday': 'अवकाश जोड़ें',
    'admin.tabWeekly': 'साप्ताहिक',
    'admin.tabLeave': 'छुट्टी और बदलाव',
    'admin.tabHolidays': 'अवकाश',
    'admin.noDoctorsRoster': 'रोस्टर पर अभी कोई डॉक्टर नहीं है।',
    'admin.weeklyNote': 'यह टेम्पलेट हर हफ़्ते दोहराया जाता है। स्लॉट बनते समय इसमें से छुट्टी और अवकाश घटा दिए जाते हैं।',
    'admin.noWeeklyTitle': 'कोई साप्ताहिक सेशन नहीं',
    'admin.noWeeklyBody': 'जब तक कोई सेशन नहीं होगा, इस डॉक्टर के लिए बुकिंग नहीं हो सकती।',
    'admin.minSlots': '{n} मिनट के स्लॉट',
    'admin.noCap': 'कोई सीमा नहीं',
    'admin.capN': 'सीमा {n}',
    'admin.off': 'बंद',
    'admin.nothingScheduledOn': '{days} को कुछ शेड्यूल नहीं है।',
    'admin.clashes_one': 'उस दिन पहले से {n} बुकिंग है। इस मरीज़ को फ़ोन करें।',
    'admin.clashes_other': 'उस दिन पहले से {n} बुकिंग हैं। इन मरीज़ों को फ़ोन करें।',
    'admin.allPhoned': 'सबको फ़ोन हो गया, इसे हटाएँ',
    'admin.overridesNote': 'एक डॉक्टर के लिए तारीख़ वाले बदलाव, आज से आगे। बंद दिन साप्ताहिक टेम्पलेट से ऊपर माना जाता है।',
    'admin.noLeaveTitle': 'कोई छुट्टी दर्ज नहीं',
    'admin.noLeaveBody': 'यह डॉक्टर साप्ताहिक टेम्पलेट के अनुसार ही बैठते हैं।',
    'admin.closedAllDay': 'पूरे दिन बंद',
    'admin.onLeave': 'छुट्टी पर',
    'admin.changedHours': 'बदला हुआ समय',
    'admin.holidaysNote': 'अवकाश के दिन पूरा क्लिनिक, सभी डॉक्टर एक साथ बंद रहते हैं। सिर्फ़ एक डॉक्टर के न होने पर छुट्टी का इस्तेमाल करें। पहले से मौजूद तारीख़ सेव करने पर दूसरा अवकाश नहीं जुड़ता, उसी का नाम बदल जाता है।',
    'admin.noHolidaysTitle': 'आगे कोई अवकाश नहीं',
    'admin.noHolidaysBody': 'त्योहारों की सूची एक बार जोड़ दें, स्लॉट अपने आप उसके हिसाब से बनेंगे।',
    'admin.addWeeklySession': 'साप्ताहिक सेशन जोड़ें',
    'admin.repeatsWeekly': 'हटाए जाने तक हर हफ़्ते दोहराया जाएगा।',
    'admin.day': 'दिन',
    'admin.session': 'सेशन',
    'admin.sessionHint': 'जैसे सुबह की OPD',
    'admin.starts': 'शुरू',
    'admin.ends': 'खत्म',
    'admin.slotMin': 'स्लॉट (मिनट)',
    'admin.maxPatients': 'अधिकतम मरीज़',
    'admin.noCapPh': 'कोई सीमा नहीं',
    'admin.capHint': 'खाली छोड़ने पर समय के हिसाब से तय होगा।',
    'admin.addSessionBtn': 'सेशन जोड़ें',
    'admin.leaveOrHours': 'छुट्टी या बदला हुआ समय',
    'admin.leaveSheetNote': 'एक तारीख़, सिर्फ़ इस डॉक्टर के लिए। पहले से बुक सब कुछ सेव करने के बाद दिखेगा।',
    'admin.reasonPh': 'कॉन्फ़्रेंस, सर्जरी लिस्ट, निजी छुट्टी',
    'admin.reasonHint': 'डेस्क पर जो भी होगा, उसे दिखेगा।',
    'admin.holidaySheetNote': 'उस तारीख़ को सभी डॉक्टरों की OPD बंद रहेगी।',
    'admin.holidayNamePh': 'दिवाली',
    'admin.addHolidayBtn': 'अवकाश जोड़ें',
    'admin.removeTitle': 'इसे हटाएँ?',
    'admin.removeNote': 'पहले से बुक अपॉइंटमेंट नहीं बदलेंगे। सिर्फ़ उपलब्ध स्लॉट बदलेंगे।',
  },
  mr: {
    'admin.title': 'ॲडमिन',
    'admin.tabReminders': 'रिमाइंडर',
    'admin.tabBackups': 'बॅकअप',
    'admin.tabAudit': 'ऑडिट',
    'admin.agoSec': '{n} सेकंदांपूर्वी',
    'admin.agoMin': '{n} मिनिटांपूर्वी',
    'admin.agoHour': '{n} तासांपूर्वी',
    'admin.agoDay': '{n} दिवसांपूर्वी',
    'admin.generate3Days': '3 दिवसांचे तयार करा',
    'admin.sendDue': 'बाकी पाठवा',
    'admin.provider': 'प्रोव्हायडर: {provider}',
    'admin.providerLogNote': '— संदेश पाठवले जात नाहीत, सर्व्हर लॉगमध्ये नोंदवले जातात.',
    'admin.queued': '{followUps} फॉलो-अप आणि {appointments} अपॉइंटमेंट रिमाइंडर रांगेत लावले.',
    'admin.generateFailed': 'तयार करता आले नाही.',
    'admin.sentSummary': '{sent} पाठवले; {failed} अयशस्वी.',
    'admin.sendFailed': 'पाठवता आले नाही.',
    'admin.cancelFailed': 'रद्द करता आले नाही.',
    'admin.backupWritten': 'बॅकअप तयार झाला: {file} ({size}).',
    'admin.backupFailed': 'बॅकअप होऊ शकला नाही.',
    'admin.downloadFailed': 'डाउनलोड होऊ शकले नाही.',
    'admin.deleteFailed': 'हटवता आले नाही.',
    'admin.restored': '{file} मधून रीस्टोर झाले. तुम्हाला पुन्हा साइन इन करावे लागू शकते.',
    'admin.restoreFailed': 'रीस्टोर होऊ शकले नाही.',
    'admin.nothingQueued': 'रांगेत काहीही नाही.',
    'admin.takeBackup': 'आता बॅकअप घ्या',
    'admin.backupDirNote': 'येथे सेव्ह होतो: {dir}. दर तीन महिन्यांनी रीस्टोर करून तपासा — कधीही रीस्टोर करून न पाहिलेला बॅकअप म्हणजे फक्त अंदाज.',
    'admin.noBackups': 'अजून एकही बॅकअप नाही.',
    'admin.restore': 'रीस्टोर',
    'admin.auditFilterAria': 'ऑडिट लॉग एंटिटीनुसार फिल्टर करा',
    'admin.auditEntityPh': 'एंटिटी — patients, visits, prescriptions…',
    'admin.clear': 'साफ करा',
    'admin.from': 'कधीपासून',
    'admin.to': 'कधीपर्यंत',
    'admin.userId': 'यूजर आयडी',
    'admin.fullUuid': 'पूर्ण uuid',
    'admin.showingOf': '{total} पैकी {shown} दाखवत आहे',
    'admin.noEntries': 'कोणतीही नोंद जुळत नाही.',
    'admin.system': 'सिस्टम',
    'admin.before': 'आधी',
    'admin.after': 'नंतर',
    'admin.cancelReminderTitle': 'हा रिमाइंडर रद्द करायचा?',
    'admin.cancelReminder': 'रिमाइंडर रद्द करा',
    'admin.deleteBackupTitle': 'हा बॅकअप हटवायचा?',
    'admin.deleteBackupNote': '{file}. याचा डेटाबेसवर काहीही परिणाम होत नाही.',
    'admin.restoreTitle': 'बॅकअपमधून रीस्टोर करा',
    'admin.restoreWarning': 'यामुळे सध्याचा डेटाबेस पुसला जाईल आणि ही फाइल पुन्हा लोड होईल. साइन इन असलेले सर्वजण साइन आउट होतील.',
    'admin.typeRestore': 'खात्री करण्यासाठी RESTORE टाइप करा',
    'admin.restoreNow': 'आता रीस्टोर करा',

    'admin.settingsSaved': 'क्लिनिक सेटिंग्ज सेव्ह झाल्या.',
    'admin.couldNotSave': 'सेव्ह करता आले नाही.',
    'admin.clinicSettings': 'क्लिनिक सेटिंग्ज',
    'admin.saveSettings': 'सेटिंग्ज सेव्ह करा',
    'admin.identity': 'ओळख',
    'admin.clinicName': 'क्लिनिकचे नाव',
    'admin.opdTimings': 'OPD वेळ',
    'admin.addressLine1': 'पत्ता ओळ 1',
    'admin.addressLine2': 'पत्ता ओळ 2',
    'admin.city': 'शहर',
    'admin.state': 'राज्य',
    'admin.pin': 'PIN कोड',
    'admin.phone': 'फोन',
    'admin.altPhone': 'पर्यायी फोन',
    'admin.numbering': 'क्रमांक आणि शेड्यूल',
    'admin.numberingNote': 'पावती क्रमांक दर आर्थिक वर्षी पुन्हा सुरू होतात. भारतात आर्थिक वर्ष साधारणपणे एप्रिलपासून सुरू होते.',
    'admin.patientCodePrefix': 'रुग्ण कोड प्रीफिक्स',
    'admin.example': 'उदा. {example}',
    'admin.receiptPrefix': 'पावती प्रीफिक्स',
    'admin.fyStart': 'आर्थिक वर्षाची सुरुवात (महिना)',
    'admin.slotLength': 'स्लॉटची लांबी (मिनिटे)',
    'admin.currency': 'चलन',

    'admin.loginCreated': 'लॉगिन तयार झाले. पहिल्यांदा साइन इन करताना यूजरला पासवर्ड बदलावा लागेल.',
    'admin.createLoginFailed': 'लॉगिन तयार करता आले नाही.',
    'admin.userDeactivated': '{name} निष्क्रिय केले.',
    'admin.userReactivated': '{name} पुन्हा सक्रिय केले.',
    'admin.couldNotUpdate': 'अपडेट करता आले नाही.',
    'admin.passwordResetDone': 'पासवर्ड रीसेट झाला.',
    'admin.resetFailed': 'पासवर्ड रीसेट करता आला नाही.',
    'admin.creating': 'तयार होत आहे…',
    'admin.createLogin': 'लॉगिन तयार करा',
    'admin.newLogin': 'नवीन लॉगिन',
    'admin.fullName': 'पूर्ण नाव',
    'admin.username': 'यूजरनेम',
    'admin.tempPassword': 'तात्पुरता पासवर्ड',
    'admin.passwordHint': 'किमान 8 अक्षरे, सर्व अंक नसावेत. यूजर पहिल्यांदा साइन इन करताना तो बदलेल.',
    'admin.passwordHintShort': 'किमान 8 अक्षरे, सर्व अंक नसावेत.',
    'admin.role': 'भूमिका',
    'admin.role.admin': 'ॲडमिन',
    'admin.role.platform_admin': 'प्लॅटफॉर्म ॲडमिन',
    'admin.role.doctor': 'डॉक्टर',
    'admin.role.receptionist': 'रिसेप्शनिस्ट',
    'admin.role.lab_manager': 'लॅब मॅनेजर',
    'admin.role.patient': 'रुग्ण',
    'admin.linkedDoctor': 'जोडलेला डॉक्टर रेकॉर्ड',
    'admin.selectDoctor': 'डॉक्टर निवडा…',
    'admin.addLogin': 'लॉगिन जोडा',
    'admin.staffLogins': 'स्टाफ लॉगिन',
    'admin.staffNote': 'लॉगिन निष्क्रिय केल्यावर ती व्यक्ती सगळीकडून लगेच साइन आउट होते. अकाउंट कधीही हटवले जात नाहीत, त्यामुळे ऑडिट नोंदी पूर्ण राहतात.',
    'admin.active': 'सक्रिय',
    'admin.inactive': 'निष्क्रिय',
    'admin.lastSignedIn': 'शेवटचे साइन इन {date}',
    'admin.neverSignedIn': 'कधीही साइन इन केले नाही',
    'admin.onTempPassword': 'तात्पुरत्या पासवर्डवर',
    'admin.resetPassword': 'पासवर्ड रीसेट करा',
    'admin.deactivate': 'निष्क्रिय करा',
    'admin.reactivate': 'पुन्हा सक्रिय करा',
    'admin.noLogins': 'अजून एकही लॉगिन नाही.',
    'admin.resetNote': '{name} सगळीकडून साइन आउट होतील आणि पुढच्या साइन इनला त्यांना स्वतःचा पासवर्ड ठेवावा लागेल.',
    'admin.newTempPassword': 'नवीन तात्पुरता पासवर्ड',
    'admin.reset': 'रीसेट करा',

    'admin.openDoctorFailed': 'या डॉक्टरची माहिती उघडता आली नाही.',
    'admin.doctorAdded': 'डॉक्टर जोडले.',
    'admin.letterheadSaved': 'लेटरहेड सेटिंग्ज सेव्ह झाल्या.',
    'admin.addDoctorCta': 'डॉक्टर जोडा',
    'admin.addDoctor': 'डॉक्टर जोडा',
    'admin.noDoctors': 'अजून एकही डॉक्टर नाही.',
    'admin.saveChanges': 'बदल सेव्ह करा',
    'admin.newDoctor': 'नवीन डॉक्टर',
    'admin.editDoctor': 'डॉक्टरची माहिती बदला',
    'admin.qualifications': 'पात्रता',
    'admin.regNo': 'मेडिकल कौन्सिल नोंदणी क्र.',
    'admin.regNoHint': 'लेटरहेडच्या हेडरमध्ये आणि सहीच्या भागात छापला जातो.',
    'admin.specialisation': 'विशेषज्ञता',
    'admin.photoUrl': 'फोटो URL',
    'admin.photoHint': 'ॲपमध्ये डॉक्टरच्या प्रोफाइलवर दिसतो. रिकामा ठेवल्यास त्यांच्या नावाची आद्याक्षरे दिसतील.',
    'admin.about': 'परिचय',
    'admin.aboutPh': 'काही वाक्ये, जी रुग्ण हे डॉक्टर निवडण्यापूर्वी वाचतात.',
    'admin.consultFee': 'सल्ला फी',
    'admin.followUpFee': 'फॉलो-अप फी',
    'admin.followUpWindow': 'फॉलो-अप कालावधी (दिवस)',
    'admin.followUpWindowHint': 'इतक्या दिवसांत पुन्हा आल्यास फॉलो-अप फी लागते.',
    'admin.activeOnRoster': 'रोस्टरवर सक्रिय',
    'admin.letterhead': 'लेटरहेड',
    'admin.letterheadNote': 'क्लिनिककडे आधीच छापलेला कागद असल्यास प्री-प्रिंटेड निवडा; मग फक्त मुख्य भाग रिकाम्या जागेत छापला जाईल. मार्जिन 210 × 297 mm शीटवर मिलिमीटरमध्ये आहेत.',
    'admin.printMode': 'प्रिंट मोड',
    'admin.modeFull': 'कोऱ्या A4 वर पूर्ण प्रिंट',
    'admin.modePreprinted': 'प्री-प्रिंटेड स्टेशनरी (फक्त मुख्य भाग)',
    'admin.marginTop': 'वर (mm)',
    'admin.marginBottom': 'खाली (mm)',
    'admin.marginLeft': 'डावीकडे (mm)',
    'admin.marginRight': 'उजवीकडे (mm)',
    'admin.headerNote': 'हेडर नोट',
    'admin.footerNote': 'फूटर नोट',
    'admin.printVitals': 'व्हायटल्सची पट्टी प्रिंट करा',
    'admin.printAllergy': 'ॲलर्जी बॅनर प्रिंट करा',
    'admin.saveLetterhead': 'लेटरहेड सेटिंग्ज सेव्ह करा',
    'admin.calibration': 'कॅलिब्रेशन',
    'admin.calibrationNote': 'टेस्ट प्रिंटमध्ये झोन बॉक्स आणि मिलिमीटर पट्टी एकाच पानावर येतात. मार्जिन एकाच वेळी सेट करण्यासाठी ते क्लिनिकच्या स्वतःच्या लेटरहेडच्या शीटवर छापा. क्लिनिकच्या प्रिंटरला जोडलेल्या डेस्कटॉपवरून हे सर्वात सोपे आहे.',
    'admin.hideTestPrint': 'टेस्ट प्रिंट लपवा',
    'admin.showTestPrint': 'टेस्ट प्रिंट दाखवा',
    'admin.printSheet': 'ही शीट प्रिंट करा',

    'admin.schedule': 'शेड्यूल',
    'admin.errNeedTimes': 'सुरुवातीची आणि शेवटची वेळ द्या.',
    'admin.errEndAfterStart': 'शेवटची वेळ सुरुवातीनंतर असायला हवी.',
    'admin.errSlotRange': 'स्लॉट 2 ते 120 मिनिटांचे असतात.',
    'admin.errCapRange': '1 ते 200 दरम्यान, किंवा मर्यादा नको असल्यास रिकामे ठेवा.',
    'admin.errPastDate': 'आजची किंवा पुढची तारीख निवडा.',
    'admin.sessionAdded': 'सेशन साप्ताहिक टेम्पलेटमध्ये जोडले.',
    'admin.addSessionFailed': 'सेशन जोडता आले नाही.',
    'admin.overrideClash_one': 'सेव्ह झाले. {n} बुकिंग हलवावे लागेल.',
    'admin.overrideClash_other': 'सेव्ह झाले. {n} बुकिंग हलवावी लागतील.',
    'admin.overrideClear': 'सेव्ह झाले. त्या दिवशी एकही बुकिंग नव्हते.',
    'admin.overrideFailed': 'हा बदल सेव्ह करता आला नाही.',
    'admin.holidaySaved': 'सुट्टी सेव्ह झाली.',
    'admin.holidayFailed': 'ही सुट्टी सेव्ह करता आली नाही.',
    'admin.removed': 'हटवले. पुढच्या लोडपासून स्लॉट पुन्हा तयार होतील.',
    'admin.removeFailed': 'हटवता आले नाही.',
    'admin.addSession': 'सेशन जोडा',
    'admin.addLeave': 'रजा किंवा बदललेली वेळ जोडा',
    'admin.addHoliday': 'सुट्टी जोडा',
    'admin.tabWeekly': 'साप्ताहिक',
    'admin.tabLeave': 'रजा आणि बदल',
    'admin.tabHolidays': 'सुट्ट्या',
    'admin.noDoctorsRoster': 'रोस्टरवर अजून एकही डॉक्टर नाही.',
    'admin.weeklyNote': 'हा टेम्पलेट दर आठवड्याला पुन्हा लागू होतो. स्लॉट तयार करताना त्यातून रजा आणि सुट्ट्या वगळल्या जातात.',
    'admin.noWeeklyTitle': 'एकही साप्ताहिक सेशन नाही',
    'admin.noWeeklyBody': 'सेशन असल्याशिवाय या डॉक्टरसाठी बुकिंग होऊ शकत नाही.',
    'admin.minSlots': '{n} मिनिटांचे स्लॉट',
    'admin.noCap': 'मर्यादा नाही',
    'admin.capN': 'मर्यादा {n}',
    'admin.off': 'बंद',
    'admin.nothingScheduledOn': '{days} रोजी काहीही शेड्यूल नाही.',
    'admin.clashes_one': 'त्या दिवशी आधीच {n} बुकिंग आहे. या रुग्णाला फोन करा.',
    'admin.clashes_other': 'त्या दिवशी आधीच {n} बुकिंग आहेत. या रुग्णांना फोन करा.',
    'admin.allPhoned': 'सर्वांना फोन झाला, हे काढा',
    'admin.overridesNote': 'एका डॉक्टरसाठी तारखेनुसार बदल, आजपासून पुढे. बंद दिवसाला साप्ताहिक टेम्पलेटपेक्षा प्राधान्य असते.',
    'admin.noLeaveTitle': 'एकही रजा नोंदलेली नाही',
    'admin.noLeaveBody': 'हे डॉक्टर साप्ताहिक टेम्पलेटनुसारच असतात.',
    'admin.closedAllDay': 'दिवसभर बंद',
    'admin.onLeave': 'रजेवर',
    'admin.changedHours': 'बदललेली वेळ',
    'admin.holidaysNote': 'सुट्टीच्या दिवशी संपूर्ण क्लिनिक, सर्व डॉक्टर एकाच वेळी बंद असतात. फक्त एक डॉक्टर नसतील तर रजा वापरा. आधीच असलेली तारीख सेव्ह केल्यास दुसरी सुट्टी जोडली जात नाही, तिचेच नाव बदलते.',
    'admin.noHolidaysTitle': 'पुढे एकही सुट्टी नाही',
    'admin.noHolidaysBody': 'सणांची यादी एकदा जोडा, स्लॉट आपोआप त्यानुसार तयार होतील.',
    'admin.addWeeklySession': 'साप्ताहिक सेशन जोडा',
    'admin.repeatsWeekly': 'हटवेपर्यंत दर आठवड्याला लागू राहील.',
    'admin.day': 'दिवस',
    'admin.session': 'सेशन',
    'admin.sessionHint': 'उदा. सकाळची OPD',
    'admin.starts': 'सुरुवात',
    'admin.ends': 'शेवट',
    'admin.slotMin': 'स्लॉट (मिनिटे)',
    'admin.maxPatients': 'कमाल रुग्ण',
    'admin.noCapPh': 'मर्यादा नाही',
    'admin.capHint': 'रिकामे ठेवल्यास वेळेनुसार ठरेल.',
    'admin.addSessionBtn': 'सेशन जोडा',
    'admin.leaveOrHours': 'रजा किंवा बदललेली वेळ',
    'admin.leaveSheetNote': 'एक तारीख, फक्त या डॉक्टरसाठी. आधीच बुक असलेले सर्व सेव्ह केल्यानंतर दिसेल.',
    'admin.reasonPh': 'कॉन्फरन्स, सर्जरी लिस्ट, वैयक्तिक रजा',
    'admin.reasonHint': 'डेस्कवर जो असेल त्याला दिसेल.',
    'admin.holidaySheetNote': 'त्या तारखेला सर्व डॉक्टरांची OPD बंद राहील.',
    'admin.holidayNamePh': 'दिवाळी',
    'admin.addHolidayBtn': 'सुट्टी जोडा',
    'admin.removeTitle': 'हे हटवायचे?',
    'admin.removeNote': 'आधीच बुक झालेल्या अपॉइंटमेंट बदलणार नाहीत. फक्त उपलब्ध स्लॉट बदलतील.',
  },
  or: {
    'admin.title': 'ଆଡମିନ୍',
    'admin.tabReminders': 'ରିମାଇଣ୍ଡର୍',
    'admin.tabBackups': 'ବ୍ୟାକଅପ୍',
    'admin.tabAudit': 'ଅଡିଟ୍',
    'admin.agoSec': '{n} ସେକେଣ୍ଡ ପୂର୍ବରୁ',
    'admin.agoMin': '{n} ମିନିଟ୍ ପୂର୍ବରୁ',
    'admin.agoHour': '{n} ଘଣ୍ଟା ପୂର୍ବରୁ',
    'admin.agoDay': '{n} ଦିନ ପୂର୍ବରୁ',
    'admin.generate3Days': '3 ଦିନର ତିଆରି କରନ୍ତୁ',
    'admin.sendDue': 'ବାକି ପଠାନ୍ତୁ',
    'admin.provider': 'ପ୍ରୋଭାଇଡର୍: {provider}',
    'admin.providerLogNote': '— ବାର୍ତ୍ତା ପଠାଯାଏ ନାହିଁ, ସର୍ଭର ଲଗ୍‌ରେ ରେକର୍ଡ ହୁଏ।',
    'admin.queued': '{followUps}ଟି ଫଲୋ-ଅପ୍ ଏବଂ {appointments}ଟି ଆପଏଣ୍ଟମେଣ୍ଟ ରିମାଇଣ୍ଡର୍ କ୍ୟୁରେ ରଖାଗଲା।',
    'admin.generateFailed': 'ତିଆରି କରିହେଲା ନାହିଁ।',
    'admin.sentSummary': '{sent}ଟି ପଠାଗଲା; {failed}ଟି ବିଫଳ।',
    'admin.sendFailed': 'ପଠାଇହେଲା ନାହିଁ।',
    'admin.cancelFailed': 'ବାତିଲ କରିହେଲା ନାହିଁ।',
    'admin.backupWritten': 'ବ୍ୟାକଅପ୍ ତିଆରି ହେଲା: {file} ({size})।',
    'admin.backupFailed': 'ବ୍ୟାକଅପ୍ ହୋଇପାରିଲା ନାହିଁ।',
    'admin.downloadFailed': 'ଡାଉନଲୋଡ୍ ହୋଇପାରିଲା ନାହିଁ।',
    'admin.deleteFailed': 'ହଟାଇହେଲା ନାହିଁ।',
    'admin.restored': '{file} ରୁ ରିଷ୍ଟୋର୍ ହେଲା। ଆପଣଙ୍କୁ ପୁଣି ସାଇନ୍ ଇନ୍ କରିବାକୁ ପଡ଼ିପାରେ।',
    'admin.restoreFailed': 'ରିଷ୍ଟୋର୍ ହୋଇପାରିଲା ନାହିଁ।',
    'admin.nothingQueued': 'କ୍ୟୁରେ କିଛି ନାହିଁ।',
    'admin.takeBackup': 'ଏବେ ବ୍ୟାକଅପ୍ ନିଅନ୍ତୁ',
    'admin.backupDirNote': 'ଏଠାରେ ସେଭ୍ ହୁଏ: {dir}। ପ୍ରତି ତିନି ମାସରେ ରିଷ୍ଟୋର୍ କରି ପରୀକ୍ଷା କରନ୍ତୁ — ଯେଉଁ ବ୍ୟାକଅପ୍ କେବେ ରିଷ୍ଟୋର୍ କରି ଦେଖାଯାଇନାହିଁ, ତାହା କେବଳ ଅନୁମାନ।',
    'admin.noBackups': 'ଏପର୍ଯ୍ୟନ୍ତ କୌଣସି ବ୍ୟାକଅପ୍ ନାହିଁ।',
    'admin.restore': 'ରିଷ୍ଟୋର୍',
    'admin.auditFilterAria': 'ଏଣ୍ଟିଟି ଅନୁସାରେ ଅଡିଟ୍ ଲଗ୍ ଫିଲ୍ଟର୍ କରନ୍ତୁ',
    'admin.auditEntityPh': 'ଏଣ୍ଟିଟି — patients, visits, prescriptions…',
    'admin.clear': 'ସଫା କରନ୍ତୁ',
    'admin.from': 'କେବେଠାରୁ',
    'admin.to': 'କେବେ ପର୍ଯ୍ୟନ୍ତ',
    'admin.userId': 'ୟୁଜର୍ ଆଇଡି',
    'admin.fullUuid': 'ପୂରା uuid',
    'admin.showingOf': '{total}ରୁ {shown}ଟି ଦେଖାଯାଉଛି',
    'admin.noEntries': 'କୌଣସି ଏଣ୍ଟ୍ରି ମେଳ ଖାଉନାହିଁ।',
    'admin.system': 'ସିଷ୍ଟମ୍',
    'admin.before': 'ପୂର୍ବ',
    'admin.after': 'ପରେ',
    'admin.cancelReminderTitle': 'ଏହି ରିମାଇଣ୍ଡର୍ ବାତିଲ କରିବେ?',
    'admin.cancelReminder': 'ରିମାଇଣ୍ଡର୍ ବାତିଲ କରନ୍ତୁ',
    'admin.deleteBackupTitle': 'ଏହି ବ୍ୟାକଅପ୍ ହଟାଇବେ?',
    'admin.deleteBackupNote': '{file}। ଏହା ଡାଟାବେସ୍‌କୁ ଛୁଏଁ ନାହିଁ।',
    'admin.restoreTitle': 'ବ୍ୟାକଅପ୍‌ରୁ ରିଷ୍ଟୋର୍ କରନ୍ତୁ',
    'admin.restoreWarning': 'ଏହା ବର୍ତ୍ତମାନର ଡାଟାବେସ୍ ମିଟାଇ ଏହି ଫାଇଲ୍ ପୁଣି ଲୋଡ୍ କରିବ। ସାଇନ୍ ଇନ୍ ଥିବା ସମସ୍ତେ ସାଇନ୍ ଆଉଟ୍ ହୋଇଯିବେ।',
    'admin.typeRestore': 'ନିଶ୍ଚିତ କରିବାକୁ RESTORE ଟାଇପ୍ କରନ୍ତୁ',
    'admin.restoreNow': 'ଏବେ ରିଷ୍ଟୋର୍ କରନ୍ତୁ',

    'admin.settingsSaved': 'କ୍ଲିନିକ୍ ସେଟିଂସ୍ ସେଭ୍ ହେଲା।',
    'admin.couldNotSave': 'ସେଭ୍ କରିହେଲା ନାହିଁ।',
    'admin.clinicSettings': 'କ୍ଲିନିକ୍ ସେଟିଂସ୍',
    'admin.saveSettings': 'ସେଟିଂସ୍ ସେଭ୍ କରନ୍ତୁ',
    'admin.identity': 'ପରିଚୟ',
    'admin.clinicName': 'କ୍ଲିନିକ୍‌ର ନାମ',
    'admin.opdTimings': 'OPD ସମୟ',
    'admin.addressLine1': 'ଠିକଣା ଧାଡ଼ି 1',
    'admin.addressLine2': 'ଠିକଣା ଧାଡ଼ି 2',
    'admin.city': 'ସହର',
    'admin.state': 'ରାଜ୍ୟ',
    'admin.pin': 'PIN କୋଡ୍',
    'admin.phone': 'ଫୋନ୍',
    'admin.altPhone': 'ବିକଳ୍ପ ଫୋନ୍',
    'admin.numbering': 'ନମ୍ବରିଂ ଓ ଶେଡ୍ୟୁଲ୍',
    'admin.numberingNote': 'ରସିଦ ନମ୍ବର ପ୍ରତି ଆର୍ଥିକ ବର୍ଷରେ ପୁଣି ଆରମ୍ଭ ହୁଏ। ଭାରତରେ ଆର୍ଥିକ ବର୍ଷ ସାଧାରଣତଃ ଏପ୍ରିଲ୍‌ରୁ ଆରମ୍ଭ ହୁଏ।',
    'admin.patientCodePrefix': 'ରୋଗୀ କୋଡ୍ ପ୍ରିଫିକ୍ସ',
    'admin.example': 'ଯେପରି {example}',
    'admin.receiptPrefix': 'ରସିଦ ପ୍ରିଫିକ୍ସ',
    'admin.fyStart': 'ଆର୍ଥିକ ବର୍ଷ ଆରମ୍ଭ (ମାସ)',
    'admin.slotLength': 'ସ୍ଲଟ୍ ଅବଧି (ମିନିଟ୍)',
    'admin.currency': 'ମୁଦ୍ରା',

    'admin.loginCreated': 'ଲଗଇନ୍ ତିଆରି ହେଲା। ପ୍ରଥମ ଥର ସାଇନ୍ ଇନ୍ ବେଳେ ୟୁଜର୍‌ଙ୍କୁ ପାସୱାର୍ଡ ବଦଳାଇବାକୁ ପଡ଼ିବ।',
    'admin.createLoginFailed': 'ଲଗଇନ୍ ତିଆରି କରିହେଲା ନାହିଁ।',
    'admin.userDeactivated': '{name}ଙ୍କୁ ନିଷ୍କ୍ରିୟ କରାଗଲା।',
    'admin.userReactivated': '{name}ଙ୍କୁ ପୁଣି ସକ୍ରିୟ କରାଗଲା।',
    'admin.couldNotUpdate': 'ଅପଡେଟ୍ କରିହେଲା ନାହିଁ।',
    'admin.passwordResetDone': 'ପାସୱାର୍ଡ ରିସେଟ୍ ହେଲା।',
    'admin.resetFailed': 'ପାସୱାର୍ଡ ରିସେଟ୍ କରିହେଲା ନାହିଁ।',
    'admin.creating': 'ତିଆରି ହେଉଛି…',
    'admin.createLogin': 'ଲଗଇନ୍ ତିଆରି କରନ୍ତୁ',
    'admin.newLogin': 'ନୂଆ ଲଗଇନ୍',
    'admin.fullName': 'ପୂରା ନାମ',
    'admin.username': 'ୟୁଜରନେମ୍',
    'admin.tempPassword': 'ଅସ୍ଥାୟୀ ପାସୱାର୍ଡ',
    'admin.passwordHint': 'ଅତିକମରେ 8 ଅକ୍ଷର, ସବୁ ଅଙ୍କ ନୁହେଁ। ୟୁଜର୍ ପ୍ରଥମ ସାଇନ୍ ଇନ୍ ବେଳେ ଏହାକୁ ବଦଳାଇବେ।',
    'admin.passwordHintShort': 'ଅତିକମରେ 8 ଅକ୍ଷର, ସବୁ ଅଙ୍କ ନୁହେଁ।',
    'admin.role': 'ଭୂମିକା',
    'admin.role.admin': 'ଆଡମିନ୍',
    'admin.role.platform_admin': 'ପ୍ଲାଟଫର୍ମ ଆଡମିନ୍',
    'admin.role.doctor': 'ଡାକ୍ତର',
    'admin.role.receptionist': 'ରିସେପ୍ସନିଷ୍ଟ',
    'admin.role.lab_manager': 'ଲ୍ୟାବ୍ ମ୍ୟାନେଜର୍',
    'admin.role.patient': 'ରୋଗୀ',
    'admin.linkedDoctor': 'ଯୋଡ଼ାଯାଇଥିବା ଡାକ୍ତର ରେକର୍ଡ',
    'admin.selectDoctor': 'ଡାକ୍ତର ବାଛନ୍ତୁ…',
    'admin.addLogin': 'ଲଗଇନ୍ ଯୋଡ଼ନ୍ତୁ',
    'admin.staffLogins': 'ଷ୍ଟାଫ୍ ଲଗଇନ୍',
    'admin.staffNote': 'ଲଗଇନ୍ ନିଷ୍କ୍ରିୟ କଲେ ସେହି ବ୍ୟକ୍ତି ସବୁଠାରୁ ତୁରନ୍ତ ସାଇନ୍ ଆଉଟ୍ ହୋଇଯାଆନ୍ତି। ଆକାଉଣ୍ଟ କେବେ ହଟାଯାଏ ନାହିଁ, ତେଣୁ ଅଡିଟ୍ ରେକର୍ଡ ଅକ୍ଷୁର୍ଣ୍ଣ ରହେ।',
    'admin.active': 'ସକ୍ରିୟ',
    'admin.inactive': 'ନିଷ୍କ୍ରିୟ',
    'admin.lastSignedIn': 'ଶେଷ ସାଇନ୍ ଇନ୍ {date}',
    'admin.neverSignedIn': 'କେବେ ସାଇନ୍ ଇନ୍ କରିନାହାନ୍ତି',
    'admin.onTempPassword': 'ଅସ୍ଥାୟୀ ପାସୱାର୍ଡରେ',
    'admin.resetPassword': 'ପାସୱାର୍ଡ ରିସେଟ୍ କରନ୍ତୁ',
    'admin.deactivate': 'ନିଷ୍କ୍ରିୟ କରନ୍ତୁ',
    'admin.reactivate': 'ପୁଣି ସକ୍ରିୟ କରନ୍ତୁ',
    'admin.noLogins': 'ଏପର୍ଯ୍ୟନ୍ତ କୌଣସି ଲଗଇନ୍ ନାହିଁ।',
    'admin.resetNote': '{name} ସବୁଠାରୁ ସାଇନ୍ ଆଉଟ୍ ହୋଇଯିବେ ଏବଂ ପରବର୍ତ୍ତୀ ସାଇନ୍ ଇନ୍‌ରେ ନିଜର ପାସୱାର୍ଡ ସେଟ୍ କରିବାକୁ ପଡ଼ିବ।',
    'admin.newTempPassword': 'ନୂଆ ଅସ୍ଥାୟୀ ପାସୱାର୍ଡ',
    'admin.reset': 'ରିସେଟ୍ କରନ୍ତୁ',

    'admin.openDoctorFailed': 'ଏହି ଡାକ୍ତରଙ୍କ ବିବରଣୀ ଖୋଲିହେଲା ନାହିଁ।',
    'admin.doctorAdded': 'ଡାକ୍ତର ଯୋଡ଼ାଗଲେ।',
    'admin.letterheadSaved': 'ଲେଟରହେଡ୍ ସେଟିଂସ୍ ସେଭ୍ ହେଲା।',
    'admin.addDoctorCta': 'ଡାକ୍ତର ଯୋଡ଼ନ୍ତୁ',
    'admin.addDoctor': 'ଡାକ୍ତର ଯୋଡ଼ନ୍ତୁ',
    'admin.noDoctors': 'ଏପର୍ଯ୍ୟନ୍ତ କୌଣସି ଡାକ୍ତର ନାହାନ୍ତି।',
    'admin.saveChanges': 'ପରିବର୍ତ୍ତନ ସେଭ୍ କରନ୍ତୁ',
    'admin.newDoctor': 'ନୂଆ ଡାକ୍ତର',
    'admin.editDoctor': 'ଡାକ୍ତରଙ୍କ ବିବରଣୀ ବଦଳାନ୍ତୁ',
    'admin.qualifications': 'ଯୋଗ୍ୟତା',
    'admin.regNo': 'ମେଡିକାଲ କାଉନସିଲ୍ ରେଜିଷ୍ଟ୍ରେସନ୍ ନଂ.',
    'admin.regNoHint': 'ଲେଟରହେଡ୍‌ର ହେଡର୍ ଓ ଦସ୍ତଖତ ଅଂଶରେ ଛପାଯାଏ।',
    'admin.specialisation': 'ବିଶେଷଜ୍ଞତା',
    'admin.photoUrl': 'ଫଟୋ URL',
    'admin.photoHint': 'ଆପ୍‌ରେ ଡାକ୍ତରଙ୍କ ପ୍ରୋଫାଇଲରେ ଦେଖାଯାଏ। ଖାଲି ଛାଡ଼ିଲେ ତାଙ୍କ ନାମର ପ୍ରଥମ ଅକ୍ଷର ଦେଖାଯିବ।',
    'admin.about': 'ପରିଚୟ',
    'admin.aboutPh': 'କିଛି ବାକ୍ୟ, ଯାହା ରୋଗୀ ଏହି ଡାକ୍ତରଙ୍କୁ ବାଛିବା ପୂର୍ବରୁ ପଢ଼ନ୍ତି।',
    'admin.consultFee': 'ପରାମର୍ଶ ଫି',
    'admin.followUpFee': 'ଫଲୋ-ଅପ୍ ଫି',
    'admin.followUpWindow': 'ଫଲୋ-ଅପ୍ ଅବଧି (ଦିନ)',
    'admin.followUpWindowHint': 'ଏତେ ଦିନ ଭିତରେ ପୁଣି ଆସିଲେ ଫଲୋ-ଅପ୍ ଫି ଲାଗେ।',
    'admin.activeOnRoster': 'ରୋଷ୍ଟର୍‌ରେ ସକ୍ରିୟ',
    'admin.letterhead': 'ଲେଟରହେଡ୍',
    'admin.letterheadNote': 'କ୍ଲିନିକ୍ ପାଖରେ ଆଗରୁ ଛପା କାଗଜ ଥିଲେ ପ୍ରି-ପ୍ରିଣ୍ଟେଡ୍ ବାଛନ୍ତୁ; ତେବେ କେବଳ ମୁଖ୍ୟ ଅଂଶ ଖାଲି ସ୍ଥାନରେ ଛପାଯିବ। ମାର୍ଜିନ୍ 210 × 297 mm ସିଟ୍‌ରେ ମିଲିମିଟରରେ।',
    'admin.printMode': 'ପ୍ରିଣ୍ଟ ମୋଡ୍',
    'admin.modeFull': 'ଖାଲି A4ରେ ପୂରା ପ୍ରିଣ୍ଟ',
    'admin.modePreprinted': 'ପ୍ରି-ପ୍ରିଣ୍ଟେଡ୍ ଷ୍ଟେସନାରୀ (କେବଳ ମୁଖ୍ୟ ଅଂଶ)',
    'admin.marginTop': 'ଉପର (mm)',
    'admin.marginBottom': 'ତଳ (mm)',
    'admin.marginLeft': 'ବାମ (mm)',
    'admin.marginRight': 'ଡାହାଣ (mm)',
    'admin.headerNote': 'ହେଡର୍ ନୋଟ୍',
    'admin.footerNote': 'ଫୁଟର୍ ନୋଟ୍',
    'admin.printVitals': 'ଭାଇଟାଲ୍ସ ପଟି ପ୍ରିଣ୍ଟ କରନ୍ତୁ',
    'admin.printAllergy': 'ଆଲର୍ଜି ବ୍ୟାନର୍ ପ୍ରିଣ୍ଟ କରନ୍ତୁ',
    'admin.saveLetterhead': 'ଲେଟରହେଡ୍ ସେଟିଂସ୍ ସେଭ୍ କରନ୍ତୁ',
    'admin.calibration': 'କ୍ୟାଲିବ୍ରେସନ୍',
    'admin.calibrationNote': 'ଟେଷ୍ଟ ପ୍ରିଣ୍ଟରେ ଜୋନ୍ ବକ୍ସ ଓ ମିଲିମିଟର ସ୍କେଲ୍ ଗୋଟିଏ ପୃଷ୍ଠାରେ ଆସେ। ମାର୍ଜିନ୍ ଥରେରେ ସେଟ୍ କରିବାକୁ ଏହାକୁ କ୍ଲିନିକ୍‌ର ନିଜ ଲେଟରହେଡ୍ ସିଟ୍‌ରେ ଛାପନ୍ତୁ। କ୍ଲିନିକ୍ ପ୍ରିଣ୍ଟର ସହ ଯୋଡ଼ା ଡେସ୍କଟପ୍‌ରୁ ଏହା ସବୁଠାରୁ ସହଜ।',
    'admin.hideTestPrint': 'ଟେଷ୍ଟ ପ୍ରିଣ୍ଟ ଲୁଚାନ୍ତୁ',
    'admin.showTestPrint': 'ଟେଷ୍ଟ ପ୍ରିଣ୍ଟ ଦେଖାନ୍ତୁ',
    'admin.printSheet': 'ଏହି ସିଟ୍ ପ୍ରିଣ୍ଟ କରନ୍ତୁ',

    'admin.schedule': 'ଶେଡ୍ୟୁଲ୍',
    'admin.errNeedTimes': 'ଆରମ୍ଭ ଓ ଶେଷ ସମୟ ଦିଅନ୍ତୁ।',
    'admin.errEndAfterStart': 'ଶେଷ ସମୟ ଆରମ୍ଭ ପରେ ହେବା ଦରକାର।',
    'admin.errSlotRange': 'ସ୍ଲଟ୍ 2ରୁ 120 ମିନିଟ୍ ହୁଏ।',
    'admin.errCapRange': '1ରୁ 200 ମଧ୍ୟରେ, କିମ୍ବା ସୀମା ନଥିଲେ ଖାଲି ଛାଡ଼ନ୍ତୁ।',
    'admin.errPastDate': 'ଆଜି ବା ପରବର୍ତ୍ତୀ ତାରିଖ ବାଛନ୍ତୁ।',
    'admin.sessionAdded': 'ସେସନ୍ ସାପ୍ତାହିକ ଟେମ୍ପଲେଟ୍‌ରେ ଯୋଡ଼ାଗଲା।',
    'admin.addSessionFailed': 'ସେସନ୍ ଯୋଡ଼ିହେଲା ନାହିଁ।',
    'admin.overrideClash_one': 'ସେଭ୍ ହେଲା। {n}ଟି ବୁକିଂ ଘୁଞ୍ଚାଇବାକୁ ପଡ଼ିବ।',
    'admin.overrideClash_other': 'ସେଭ୍ ହେଲା। {n}ଟି ବୁକିଂ ଘୁଞ୍ଚାଇବାକୁ ପଡ଼ିବ।',
    'admin.overrideClear': 'ସେଭ୍ ହେଲା। ସେଦିନ କୌଣସି ବୁକିଂ ନଥିଲା।',
    'admin.overrideFailed': 'ଏହି ପରିବର୍ତ୍ତନ ସେଭ୍ କରିହେଲା ନାହିଁ।',
    'admin.holidaySaved': 'ଛୁଟିଦିନ ସେଭ୍ ହେଲା।',
    'admin.holidayFailed': 'ଏହି ଛୁଟିଦିନ ସେଭ୍ କରିହେଲା ନାହିଁ।',
    'admin.removed': 'ହଟାଗଲା। ପରବର୍ତ୍ତୀ ଲୋଡ୍‌ରୁ ସ୍ଲଟ୍ ପୁଣି ତିଆରି ହେବ।',
    'admin.removeFailed': 'ହଟାଇହେଲା ନାହିଁ।',
    'admin.addSession': 'ସେସନ୍ ଯୋଡ଼ନ୍ତୁ',
    'admin.addLeave': 'ଛୁଟି ବା ବଦଳିଥିବା ସମୟ ଯୋଡ଼ନ୍ତୁ',
    'admin.addHoliday': 'ଛୁଟିଦିନ ଯୋଡ଼ନ୍ତୁ',
    'admin.tabWeekly': 'ସାପ୍ତାହିକ',
    'admin.tabLeave': 'ଛୁଟି ଓ ପରିବର୍ତ୍ତନ',
    'admin.tabHolidays': 'ଛୁଟିଦିନ',
    'admin.noDoctorsRoster': 'ରୋଷ୍ଟର୍‌ରେ ଏପର୍ଯ୍ୟନ୍ତ କୌଣସି ଡାକ୍ତର ନାହାନ୍ତି।',
    'admin.weeklyNote': 'ଏହି ଟେମ୍ପଲେଟ୍ ପ୍ରତି ସପ୍ତାହ ଦୋହରାଯାଏ। ସ୍ଲଟ୍ ତିଆରି ବେଳେ ଏଥିରୁ ଛୁଟି ଓ ଛୁଟିଦିନ ବାଦ୍ ଦିଆଯାଏ।',
    'admin.noWeeklyTitle': 'କୌଣସି ସାପ୍ତାହିକ ସେସନ୍ ନାହିଁ',
    'admin.noWeeklyBody': 'ସେସନ୍ ନଥିବା ପର୍ଯ୍ୟନ୍ତ ଏହି ଡାକ୍ତରଙ୍କ ପାଇଁ ବୁକିଂ ହୋଇପାରିବ ନାହିଁ।',
    'admin.minSlots': '{n} ମିନିଟ୍‌ର ସ୍ଲଟ୍',
    'admin.noCap': 'କୌଣସି ସୀମା ନାହିଁ',
    'admin.capN': 'ସୀମା {n}',
    'admin.off': 'ବନ୍ଦ',
    'admin.nothingScheduledOn': '{days}ରେ କିଛି ଶେଡ୍ୟୁଲ୍ ନାହିଁ।',
    'admin.clashes_one': 'ସେଦିନ ଆଗରୁ {n}ଟି ବୁକିଂ ଅଛି। ଏହି ରୋଗୀଙ୍କୁ ଫୋନ୍ କରନ୍ତୁ।',
    'admin.clashes_other': 'ସେଦିନ ଆଗରୁ {n}ଟି ବୁକିଂ ଅଛି। ଏହି ରୋଗୀମାନଙ୍କୁ ଫୋନ୍ କରନ୍ତୁ।',
    'admin.allPhoned': 'ସମସ୍ତଙ୍କୁ ଫୋନ୍ ହେଲା, ଏହାକୁ ହଟାନ୍ତୁ',
    'admin.overridesNote': 'ଜଣେ ଡାକ୍ତରଙ୍କ ପାଇଁ ତାରିଖ ଅନୁସାରେ ପରିବର୍ତ୍ତନ, ଆଜିଠାରୁ ଆଗକୁ। ବନ୍ଦ ଦିନ ସାପ୍ତାହିକ ଟେମ୍ପଲେଟ୍ ଉପରେ ପ୍ରାଧାନ୍ୟ ପାଏ।',
    'admin.noLeaveTitle': 'କୌଣସି ଛୁଟି ନାହିଁ',
    'admin.noLeaveBody': 'ଏହି ଡାକ୍ତର ସାପ୍ତାହିକ ଟେମ୍ପଲେଟ୍ ଅନୁସାରେ ହିଁ ବସନ୍ତି।',
    'admin.closedAllDay': 'ସାରା ଦିନ ବନ୍ଦ',
    'admin.onLeave': 'ଛୁଟିରେ',
    'admin.changedHours': 'ବଦଳିଥିବା ସମୟ',
    'admin.holidaysNote': 'ଛୁଟିଦିନରେ ପୂରା କ୍ଲିନିକ୍, ସମସ୍ତ ଡାକ୍ତର ଏକାସାଙ୍ଗରେ ବନ୍ଦ ରହନ୍ତି। କେବଳ ଜଣେ ଡାକ୍ତର ନଥିଲେ ଛୁଟି ବ୍ୟବହାର କରନ୍ତୁ। ଆଗରୁ ଥିବା ତାରିଖ ସେଭ୍ କଲେ ଦ୍ୱିତୀୟଟି ଯୋଡ଼ା ହୁଏ ନାହିଁ, ତାହାର ନାମ ବଦଳିଯାଏ।',
    'admin.noHolidaysTitle': 'ଆଗକୁ କୌଣସି ଛୁଟିଦିନ ନାହିଁ',
    'admin.noHolidaysBody': 'ପର୍ବପର୍ବାଣିର ତାଲିକା ଥରେ ଯୋଡ଼ନ୍ତୁ, ସ୍ଲଟ୍ ଆପେ ସେହି ଅନୁସାରେ ତିଆରି ହେବ।',
    'admin.addWeeklySession': 'ସାପ୍ତାହିକ ସେସନ୍ ଯୋଡ଼ନ୍ତୁ',
    'admin.repeatsWeekly': 'ହଟାଯିବା ପର୍ଯ୍ୟନ୍ତ ପ୍ରତି ସପ୍ତାହ ଦୋହରାଯିବ।',
    'admin.day': 'ଦିନ',
    'admin.session': 'ସେସନ୍',
    'admin.sessionHint': 'ଯେପରି ସକାଳ OPD',
    'admin.starts': 'ଆରମ୍ଭ',
    'admin.ends': 'ଶେଷ',
    'admin.slotMin': 'ସ୍ଲଟ୍ (ମିନିଟ୍)',
    'admin.maxPatients': 'ସର୍ବାଧିକ ରୋଗୀ',
    'admin.noCapPh': 'କୌଣସି ସୀମା ନାହିଁ',
    'admin.capHint': 'ଖାଲି ଛାଡ଼ିଲେ ସମୟ ଅନୁସାରେ ସ୍ଥିର ହେବ।',
    'admin.addSessionBtn': 'ସେସନ୍ ଯୋଡ଼ନ୍ତୁ',
    'admin.leaveOrHours': 'ଛୁଟି ବା ବଦଳିଥିବା ସମୟ',
    'admin.leaveSheetNote': 'ଗୋଟିଏ ତାରିଖ, କେବଳ ଏହି ଡାକ୍ତରଙ୍କ ପାଇଁ। ଆଗରୁ ବୁକ୍ ହୋଇଥିବା ସବୁ ସେଭ୍ ପରେ ଦେଖାଯିବ।',
    'admin.reasonPh': 'କନଫରେନ୍ସ, ସର୍ଜରୀ ତାଲିକା, ବ୍ୟକ୍ତିଗତ ଛୁଟି',
    'admin.reasonHint': 'ଡେସ୍କରେ ଯିଏ ଥିବେ, ତାଙ୍କୁ ଦେଖାଯିବ।',
    'admin.holidaySheetNote': 'ସେହି ତାରିଖରେ ସମସ୍ତ ଡାକ୍ତରଙ୍କ OPD ବନ୍ଦ ରହିବ।',
    'admin.holidayNamePh': 'ଦୀପାବଳି',
    'admin.addHolidayBtn': 'ଛୁଟିଦିନ ଯୋଡ଼ନ୍ତୁ',
    'admin.removeTitle': 'ଏହାକୁ ହଟାଇବେ?',
    'admin.removeNote': 'ଆଗରୁ ବୁକ୍ ହୋଇଥିବା ଆପଏଣ୍ଟମେଣ୍ଟ ବଦଳିବ ନାହିଁ। କେବଳ ଉପଲବ୍ଧ ସ୍ଲଟ୍ ବଦଳିବ।',
  },
})
